import { SystemHealthAPI } from '@API/data-services/system-health-service'
import { Routes } from '@Components/main-app/types'
import { LoadingRows } from '@Components/util/loading-rows'
import { formatEnumLabel } from '@Components/util/utils'
import { ScanStatus } from '@Generated/back-office-client/src'
import { Badge, Flex, Heading, Skeleton, Table, Text } from '@radix-ui/themes'
import { useQuery } from '@tanstack/react-query'
import { formatInTimeZone } from 'date-fns-tz'
import { Link } from 'react-router-dom'
import { ErrorMessagesTable } from './error-messages-table'
import { HealthStats } from './health-stats'
import { HealthTimeline } from './health-timeline'
import { FAILURE_RATE_ALERT_THRESHOLD, formatRate, HealthStat, REFRESH_INTERVAL_MS } from './types'
import { useHealthSearchParams } from './use-health-search-params'

export const ScanHealthTab = ({
  windowHours,
  timezone,
}: {
  windowHours: number
  timezone: string
}) => {
  const { currentHref } = useHealthSearchParams()
  const { data, isLoading, isError, dataUpdatedAt } = useQuery({
    queryKey: ['ticket-scan-health', windowHours],
    staleTime: 0,
    refetchInterval: REFRESH_INTERVAL_MS,
    queryFn: () => SystemHealthAPI.getTicketScanHealth(windowHours),
  })

  const stats: HealthStat[] = [
    { label: 'Total', value: `${data?.total ?? 0}` },
    {
      label: 'Error rate',
      value: formatRate(data?.errorRate ?? 0),
      alert: (data?.errorRate ?? 0) > FAILURE_RATE_ALERT_THRESHOLD,
    },
    ...(data?.statusCounts ?? []).map((statusCount) => ({
      label: formatEnumLabel(statusCount.status),
      value: `${statusCount.count}`,
    })),
  ]

  return (
    <Flex direction="column" gap="4">
      {dataUpdatedAt ? (
        <Text size="2" color="gray">
          Updated {formatInTimeZone(dataUpdatedAt, timezone, 'p')}, refreshes every minute
        </Text>
      ) : null}

      {isError ? <Text color="red">Error loading ticket scan health</Text> : null}

      <HealthStats stats={stats} loading={isLoading} />

      {data ? (
        <HealthTimeline
          buckets={data.buckets}
          bucketMinutes={data.bucketMinutes}
          timezone={timezone}
          otherLabel="Other scans"
        />
      ) : null}

      <ErrorMessagesTable
        errorMessages={data?.errorMessages ?? []}
        timezone={timezone}
        loading={isLoading}
      />

      <Heading size="4">Recent scans</Heading>
      <Skeleton loading={isLoading}>
        <Table.Root>
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell>Time</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Player / device</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Ticket type</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Details</Table.ColumnHeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {isLoading ? (
              <LoadingRows />
            ) : (
              data?.recentScans.map((scan) => (
                <Table.Row key={scan.scanId}>
                  <Table.Cell>{formatInTimeZone(scan.createdAt, timezone, 'PP p')}</Table.Cell>
                  <Table.Cell>
                    {scan.playerLookupId ? (
                      <Link
                        to={Routes.PLAYER_DETAILS_SCANS.replace(':lookupId', scan.playerLookupId)}
                        state={{ from: currentHref }}>
                        {scan.playerLookupId}
                      </Link>
                    ) : (
                      (scan.deviceLookupId ?? 'N/A')
                    )}
                  </Table.Cell>
                  <Table.Cell>{formatEnumLabel(scan.ticketType)}</Table.Cell>
                  <Table.Cell>
                    <Badge color={scan.status === ScanStatus.ERROR ? 'red' : 'gray'}>
                      {formatEnumLabel(scan.status)}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell>{scan.details}</Table.Cell>
                </Table.Row>
              ))
            )}
          </Table.Body>
        </Table.Root>
      </Skeleton>
    </Flex>
  )
}
